// ===========================================
// Rich Text Editor Component
// ===========================================
// React Quill-based rich text editor with server-side image upload support
// Images are uploaded to the server and embedded via URL for email compatibility
// Tables use Quill 2's own table module; its commands have no toolbar UI, so the
// toolbar's table button opens TableMenu. The send path styles them for mail
// clients (`prepareRichHtmlForEmail`, backend).

import { useMemo, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Box, Typography } from '@mui/material';
import TableMenu, { type TableCommand } from './RichTextEditorTableMenu';
import { uploadEmailImage } from '../../api/customEmail.api';

const Delta = Quill.import('delta') as typeof import('quill-delta').default;

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
}

interface RichTextEditorProps {
  value: string;
  /**
   * `source` is Quill's: `'user'` for typing, toolbar and merge-chip inserts; `'api'` when Quill
   * re-renders a value it was GIVEN (it normalises the HTML on load and still fires this). A caller
   * that must tell an edit from that normalisation reads it (System Emails, #315).
   */
  onChange: (content: string, source?: 'user' | 'api' | 'silent') => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  /** Enable server-side image uploads (for email compatibility) */
  enableImageUpload?: boolean;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter your content here...',
  height = 400,
  disabled = false,
  label,
  helperText,
  enableImageUpload = true,
}, ref) {
  const quillRef = useRef<ReactQuill>(null);
  const [tableMenu, setTableMenu] = useState<{ anchor: HTMLElement; inTable: boolean } | null>(null);
  // Opening the menu takes focus from the editor; each command restores the cursor first.
  const tableRangeRef = useRef<{ index: number; length: number } | null>(null);

  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      const range = quill.getSelection(true);
      quill.insertText(range.index, text, 'user');
      quill.setSelection(range.index + text.length, 0);
    },
  }));

  // Custom image handler that uploads to server
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image is too large. Maximum size is 5MB.');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
        return;
      }

      try {
        // Show loading state (optional: could add a loading indicator)
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        // Get cursor position
        const range = quill.getSelection(true);

        // Upload the image to the server
        const result = await uploadEmailImage(file);

        // Insert the image at the cursor position using the server URL
        quill.insertEmbed(range.index, 'image', result.url);

        // Move cursor to after the image
        quill.setSelection(range.index + 1, 0);
      } catch (error) {
        console.error('Failed to upload image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };
  }, []);

  // The toolbar's table button. Quill calls this with the toolbar as `this`; the
  // button is found through the editor so the handler stays stable (a changed
  // `modules` object re-creates the editor).
  const tableHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    tableRangeRef.current = range;
    const button = (quill.getModule('toolbar') as { container: HTMLElement }).container.querySelector<HTMLElement>(
      'button.ql-table'
    );
    if (!button) return;
    const [line] = quill.getLine(range.index);
    const inTable = (line as { statics?: { blotName?: string } } | null)?.statics?.blotName === 'table';
    setTableMenu({ anchor: button, inTable });
  }, []);

  const runTableCommand = useCallback((command: TableCommand) => {
    setTableMenu(null);
    const quill = quillRef.current?.getEditor();
    const range = tableRangeRef.current;
    if (!quill || !range) return;
    quill.setSelection(range.index, range.length, 'silent');
    const table = quill.getModule('table') as {
      insertTable: (rows: number, columns: number) => void;
      insertRowAbove: () => void;
      insertRowBelow: () => void;
      insertColumnLeft: () => void;
      insertColumnRight: () => void;
      deleteRow: () => void;
      deleteColumn: () => void;
      deleteTable: () => void;
    };
    switch (command.type) {
      case 'insert': {
        // Quill inserts the table AT the cursor, so the text of the cursor's line
        // before it becomes the first cell (and a mid-line cursor splits the line).
        // A line with text gets the table on a fresh plain line after it instead.
        const [line] = quill.getLine(range.index);
        if (line && line.length() > 1) {
          const after = quill.getIndex(line) + line.length();
          quill.updateContents(new Delta().retain(after).insert('\n'), 'user');
          quill.setSelection(after, 0, 'silent');
        }
        table.insertTable(command.rows, command.columns);
        break;
      }
      case 'rowAbove':
        table.insertRowAbove();
        break;
      case 'rowBelow':
        table.insertRowBelow();
        break;
      case 'columnLeft':
        table.insertColumnLeft();
        break;
      case 'columnRight':
        table.insertColumnRight();
        break;
      case 'deleteRow':
        table.deleteRow();
        break;
      case 'deleteColumn':
        table.deleteColumn();
        break;
      case 'deleteTable':
        table.deleteTable();
        break;
    }
  }, []);

  // Quill modules configuration
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link', 'image', 'table'],
          ['blockquote', 'code-block'],
          ['clean'],
        ],
        handlers: {
          table: tableHandler,
          ...(enableImageUpload ? { image: imageHandler } : {}),
        },
      },
      table: true,
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler, tableHandler, enableImageUpload]
  );

  // Quill formats
  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'indent',
    'link',
    'image',
    'blockquote',
    'code-block',
    // Registers the row, body and table containers with it.
    'table',
  ];

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.5, fontWeight: 500 }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          '& .quill': {
            display: 'flex',
            flexDirection: 'column',
          },
          '& .ql-toolbar': {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          },
          '& .ql-container': {
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            borderColor: 'divider',
            fontSize: '14px',
            fontFamily: 'inherit',
            minHeight: height,
            backgroundColor: '#ffffff',
          },
          '& .ql-editor': {
            minHeight: height - 42, // Account for toolbar
            lineHeight: 1.6,
          },
          '& .ql-editor.ql-blank::before': {
            fontStyle: 'normal',
            color: 'text.secondary',
          },
          '& .ql-editor a': {
            color: '#4580E4',
          },
          '& .ql-editor img': {
            maxWidth: '100%',
            height: 'auto',
          },
          // Match the borders the email gets (richHtmlForEmail.ts).
          '& .ql-editor td': {
            border: '1px solid #d0d5dc',
            padding: '6px 10px',
          },
          // Disabled state
          ...(disabled && {
            opacity: 0.6,
            pointerEvents: 'none',
          }),
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={(content, _delta, source, editor) => {
            // Get the text content without HTML to check if it's truly empty
            const text = editor.getText().trim();
            // Images and tables (a new one has only empty cells) are content with no text
            const hasEmbeds = /<(img|table)\b/i.test(content);
            // If the editor is empty (just whitespace) AND has no images or tables, pass empty string
            // Otherwise pass the HTML content
            if (text.length === 0 && !hasEmbeds) {
              onChange('', source);
            } else {
              onChange(content, source);
            }
          }}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
        />
      </Box>
      <TableMenu
        anchorEl={tableMenu?.anchor ?? null}
        inTable={tableMenu?.inTable ?? false}
        onClose={() => setTableMenu(null)}
        onCommand={runTableCommand}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

export default RichTextEditor;
